<template>
  <div>
    <b-form class="mb-2" @submit.prevent="search">
      <b-form-row>
        <b-col md="2" class="mb-2">
          <label class="small mb-0">ตั้งแต่</label>
          <b-form-input v-model="filters.startDt" type="date" size="sm" />
        </b-col>
        <b-col md="2" class="mb-2">
          <label class="small mb-0">ถึง</label>
          <b-form-input v-model="filters.endDt" type="date" size="sm" />
        </b-col>
        <b-col md="3" class="mb-2">
          <label class="small mb-0">ผู้ใช้</label>
          <b-form-select v-model="filters.uid" :options="userSelectOptions" size="sm" />
        </b-col>
        <b-col md="3" class="mb-2">
          <label class="small mb-0">เครื่องมือ</label>
          <b-form-select v-model="filters.tool" :options="toolOptions" size="sm" />
        </b-col>
        <b-col md="2" class="mb-2">
          <label class="small mb-0">HN คนไข้</label>
          <b-form-input v-model="filters.hn" size="sm" maxlength="45" placeholder="เช่น 69001" />
        </b-col>
        <b-col md="2" class="mb-2">
          <label class="small mb-0">รหัสคนไข้</label>
          <b-form-input
            v-model="filters.patientId"
            size="sm"
            type="number"
            min="1"
            step="1"
            placeholder="เช่น 12345"
          />
        </b-col>
      </b-form-row>
      <div class="d-flex flex-wrap align-items-center">
        <b-button type="submit" size="sm" variant="primary" class="mr-2" :disabled="isLoading">ค้นหา</b-button>
        <b-button size="sm" variant="outline-secondary" class="mr-2" @click="clearFilters">ล้าง</b-button>
        <small class="text-muted">เลือกช่วงได้ไม่เกิน 31 วัน (ค้นหาด้วย HN หรือรหัสคนไข้ได้ถึง 366 วัน)</small>
      </div>
    </b-form>

    <b-alert v-if="errorMessage" show variant="danger">{{ errorMessage }}</b-alert>
    <b-alert v-if="resolvedPatientIds && resolvedPatientIds.length === 0" show variant="secondary">
      ไม่พบคนไข้ตาม HN หรือรหัสคนไข้นี้ในคลินิก
    </b-alert>
    <b-alert v-if="patientIdsTruncated" show variant="warning">
      HN นี้ตรงกับคนไข้มากกว่า 20 คน แสดงเฉพาะ 20 คนแรก กรุณาค้นหาด้วยรหัสคนไข้เพื่อผลที่ครบถ้วน
    </b-alert>

    <b-table
      :items="items"
      :fields="fields"
      :busy="isLoading"
      small
      hover
      responsive
      show-empty
      empty-text="ไม่พบการเรียกใช้ในช่วงที่เลือก"
    >
      <template #table-busy>
        <div class="text-center text-muted my-2">
          <b-spinner small class="align-middle mr-2"></b-spinner>
          <strong>กำลังโหลด...</strong>
        </div>
      </template>
      <template #cell(at)="row">{{ formatDateTime(row.item.at) }}</template>
      <template #cell(user)="row">
        <div>{{ row.item.userName || `uid ${row.item.uid}` }}</div>
        <small class="text-muted">{{ row.item.clientName || "" }}</small>
      </template>
      <template #cell(tool)="row">
        <div>{{ toolLabel(row.item.tool) }}</div>
        <small class="text-muted">{{ row.item.tool }}</small>
      </template>
      <template #cell(status)="row">
        <b-badge :variant="statusLabel(row.item.status).variant">{{ statusLabel(row.item.status).text }}</b-badge>
        <small v-if="row.item.errorCode" class="d-block text-muted">{{ row.item.errorCode }}</small>
      </template>
      <template #cell(patients)="row">
        {{ (row.item.patientIds || []).length ? `${row.item.patientIds.length} คน` : "-" }}
      </template>
      <template #cell(details)="row">
        <b-button size="sm" variant="link" class="p-0" @click="row.toggleDetails">
          {{ row.detailsShowing ? "ซ่อน" : "รายละเอียด" }}
        </b-button>
      </template>
      <template #row-details="row">
        <div class="small">
          <div>รหัสคนไข้: {{ (row.item.patientIds || []).length ? row.item.patientIds.join(", ") : "-" }}</div>
          <div>นับโควตา: {{ row.item.metered ? "ใช่" : "ไม่" }} · เวลาประมวลผล {{ row.item.latencyMs }} ms</div>
          <!-- args come back from the API already trimmed (≤500 chars, ruling D4); render
               compactly (single line, no enrichment) rather than a large pretty-printed dump. -->
          <code class="d-block mt-1 p-2 bg-light rounded text-break">{{ formatArgs(row.item.args) }}</code>
        </div>
      </template>
    </b-table>

    <div class="d-flex justify-content-between align-items-center">
      <b-button size="sm" variant="outline-secondary" :disabled="offset === 0 || isLoading" @click="prevPage">
        ก่อนหน้า
      </b-button>
      <small class="text-muted">รายการที่ {{ items.length ? offset + 1 : 0 }}–{{ offset + items.length }}</small>
      <b-button size="sm" variant="outline-secondary" :disabled="!hasMore || atOffsetLimit || isLoading" @click="nextPage">
        ถัดไป
      </b-button>
    </div>
    <small v-if="hasMore && atOffsetLimit" class="d-block text-right text-muted mt-1">
      แสดงได้สูงสุด 5,000 รายการ กรุณาแคบช่วงวันที่เพื่อดูรายการเพิ่มเติม
    </small>
  </div>
</template>

<script>
import moment from "moment";
import { mcpErrorMessage } from "@/components/mcp/mcpErrors";
import { MCP_TOOL_LABELS, MCP_STATUS_LABELS } from "@/components/mcp/mcpLabels";

const PAGE_SIZE = 50;
const MAX_OFFSET = 5000;

const defaultFilters = () => ({
  startDt: moment().subtract(6, "days").format("YYYY-MM-DD"),
  endDt: moment().format("YYYY-MM-DD"),
  uid: null,
  tool: null,
  hn: "",
  patientId: "",
});

export default {
  name: "McpAuditLog",
  props: {
    clinicUrl: { type: String, required: true },
    users: { type: Array, default: () => [] },
  },
  data() {
    return {
      filters: defaultFilters(),
      applied: null,
      items: [],
      offset: 0,
      hasMore: false,
      isLoading: false,
      errorMessage: "",
      resolvedPatientIds: null,
      patientIdsTruncated: false,
      fields: [
        { key: "at", label: "เวลา" },
        { key: "user", label: "ผู้ใช้ / แอป AI" },
        { key: "tool", label: "เครื่องมือ" },
        { key: "status", label: "ผลลัพธ์" },
        { key: "rowCount", label: "จำนวนแถว" },
        { key: "patients", label: "คนไข้" },
        { key: "details", label: "" },
      ],
    };
  },
  computed: {
    atOffsetLimit() {
      return this.offset + PAGE_SIZE > MAX_OFFSET;
    },
    userSelectOptions() {
      const labels = new Map();
      this.users.forEach(user => {
        if (user && user.uid && !labels.has(user.uid)) {
          labels.set(user.uid, user.name || user.email || `uid ${user.uid}`);
        }
      });
      return [
        { value: null, text: "ผู้ใช้ทั้งหมด" },
        ...[...labels.entries()].map(([value, text]) => ({ value, text })),
      ];
    },
    toolOptions() {
      return [
        { value: null, text: "ทุกเครื่องมือ" },
        ...Object.entries(MCP_TOOL_LABELS).map(([value, text]) => ({ value, text })),
      ];
    },
  },
  created() {
    this.search();
  },
  methods: {
    formatDateTime(value) {
      return value ? moment(value).format("DD/MM/YYYY HH:mm:ss") : "-";
    },
    toolLabel(tool) {
      return MCP_TOOL_LABELS[tool] || tool;
    },
    statusLabel(status) {
      return MCP_STATUS_LABELS[status] || { text: status, variant: "secondary" };
    },
    formatArgs(args) {
      try {
        // Compact single-line rendering (ruling D4): show exactly what the API
        // returned, never re-expand or enrich it (e.g. no looking up a patient's
        // national ID/phone from an id present in args).
        // Args longer than 500 chars arrive already trimmed as a string.
        if (typeof args === "string") return args;
        return JSON.stringify(args || {});
      } catch (error) {
        return "-";
      }
    },
    validate() {
      const start = moment(this.filters.startDt, "YYYY-MM-DD", true);
      const end = moment(this.filters.endDt, "YYYY-MM-DD", true);
      if (!start.isValid() || !end.isValid()) return "กรุณาเลือกวันที่ให้ครบ";
      if (start.year() >= 2400 || end.year() >= 2400) return "กรุณาใช้ปี ค.ศ. (เช่น 2026)";
      const days = end.diff(start, "days") + 1;
      if (days < 1) return "วันที่เริ่มต้องไม่เกินวันที่สิ้นสุด";
      const patientIdText = String(this.filters.patientId).trim();
      if (patientIdText && !/^[1-9]\d*$/.test(patientIdText)) {
        return "รหัสคนไข้ต้องเป็นจำนวนเต็มบวก";
      }
      const byHn = Boolean(this.filters.hn.trim());
      const byPatientId = Boolean(patientIdText);
      if (days > (byHn || byPatientId ? 366 : 31)) {
        return byHn || byPatientId
          ? "ค้นหาด้วย HN หรือรหัสคนไข้ได้ไม่เกิน 366 วัน"
          : "เลือกช่วงวันที่ได้ไม่เกิน 31 วัน (ค้นหาด้วย HN หรือรหัสคนไข้ได้ถึง 366 วัน)";
      }
      return "";
    },
    search() {
      this.errorMessage = this.validate();
      if (this.errorMessage) return;
      const params = { startDt: this.filters.startDt, endDt: this.filters.endDt };
      if (this.filters.uid !== null) params.uid = this.filters.uid;
      if (this.filters.tool !== null) params.tool = this.filters.tool;
      if (this.filters.hn.trim()) params.hn = this.filters.hn.trim();
      const patientIdText = String(this.filters.patientId).trim();
      if (patientIdText) params.patientId = patientIdText;
      this.applied = params;
      this.offset = 0;
      this.fetchPage();
    },
    clearFilters() {
      this.filters = defaultFilters();
      this.search();
    },
    async fetchPage() {
      this.isLoading = true;
      this.errorMessage = "";
      try {
        const data = await this.$store.dispatch("moduleMcpSetting/fetchAuditLogs", {
          clinicUrl: this.clinicUrl,
          params: { ...this.applied, offset: this.offset, limit: PAGE_SIZE },
        });
        this.items = data.items;
        this.hasMore = data.hasMore;
        this.resolvedPatientIds = data.filters.patientIds;
        this.patientIdsTruncated = Boolean(data.filters.patientIdsTruncated);
      } catch (error) {
        this.errorMessage = mcpErrorMessage(error, "โหลดบันทึกการใช้งานไม่สำเร็จ");
      } finally {
        this.isLoading = false;
      }
    },
    nextPage() {
      this.offset += PAGE_SIZE;
      this.fetchPage();
    },
    prevPage() {
      this.offset = Math.max(this.offset - PAGE_SIZE, 0);
      this.fetchPage();
    },
  },
};
</script>
